import React, { useState, useEffect } from 'react';
import ImagePickerModal from './shared/ImagePickerModal';
import { selectImagesForTask, updateHeroImage } from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import './EditableSocialMediaViewer.css';

interface PlatformData {
  post?: string;
  caption?: string;
  tweet?: string;
  thread?: string[];
  hashtags?: string[];
  cta?: string;
}

interface SocialMediaData {
  article_url?: string;
  linkedin?: PlatformData;
  twitter?: PlatformData;
  facebook?: PlatformData;
  instagram?: PlatformData;
}

interface EditableSocialMediaViewerProps {
  rawContent: string;
  // May return `false` (sync or via Promise) to signal a refused save (e.g.
  // ContentPreview's wrapper guard) — the caller must not clear its dirty
  // state in that case. Returning void/true/undefined means "saved".
  onSave: (updatedRaw: string) => void | boolean | Promise<void | boolean>;
  onCancel: () => void;
  articleImageUrl?: string;
  articleImageAlt?: string;
  /** Task ID — when supplied, enables the "Change Hero Image" button in Article Details. */
  taskId?: string;
  /** Called after the hero image is successfully persisted so the parent can refresh. */
  onHeroImageChanged?: (newUrl: string) => void;
}

/** Fix Python serialisation artefacts so JSON.parse succeeds:
 *  - \' is not a valid JSON escape sequence → replace with '
 *  - Leading/trailing literal \n, \t, \r sequences (not whitespace) must be stripped */
const unescapeLiteral = (s: string): string =>
  s.replace(/\\'/g, "'")
   .replace(/^(\\n|\\t|\\r|\s)+/, '')
   .replace(/(\\n|\\t|\\r|\s)+$/, '');

/** Convert Python-style single-quoted dict string to a double-quoted JSON string (best-effort) */
const pythonDictToJson = (s: string): string => {
  try {
    // eslint-disable-next-line no-new-func
    return JSON.stringify(Function('"use strict"; return (' + s + ')')());
  } catch (_) {
    return s;
  }
};

const isSocialData = (o: any): boolean =>
  o && typeof o === 'object' && (o.linkedin || o.twitter || o.facebook || o.instagram);

const parseSocialMediaContent = (raw: string): { data: SocialMediaData; wrapper: any } | null => {
  const trimmed = (raw || '').trim();

  // 1. Direct JSON
  try {
    const parsed = JSON.parse(trimmed);
    if (isSocialData(parsed)) {
      return { data: parsed, wrapper: null };
    }
    // Nested under a "result" or "response" key (may contain ```json...``` fences)
    const inner = parsed.result ?? parsed.response ?? null;
    if (typeof inner === 'string') {
      const fenceMatch = inner.match(/```json\s*([\s\S]*?)```/);
      const jsonStr = fenceMatch ? unescapeLiteral(fenceMatch[1]) : unescapeLiteral(inner);
      try {
        const nested = JSON.parse(jsonStr);
        if (isSocialData(nested)) {
          return { data: nested, wrapper: parsed };
        }
      } catch (_) { /* fall through */ }
    }
    // Direct object with social keys at top level via other wrapper
    if (typeof parsed.response === 'object' && parsed.response !== null) {
      if (isSocialData(parsed.response)) {
        return { data: parsed.response, wrapper: parsed };
      }
    }
  } catch (_) { /* fall through */ }

  // 2. Raw ```json fence at the top level (no outer wrapper)
  const topFence = trimmed.match(/```json\s*([\s\S]*?)```/);
  if (topFence) {
    try {
      const parsed = JSON.parse(unescapeLiteral(topFence[1]));
      if (isSocialData(parsed)) {
        return { data: parsed, wrapper: null };
      }
    } catch (_) { /* fall through */ }
  }

  // 3. Python-style single-quoted dict — evaluate as JS (valid JS object literal syntax)
  if (trimmed.startsWith("{'") || trimmed.startsWith("{ '")) {
    try {
      // eslint-disable-next-line no-new-func
      const obj = Function('"use strict"; return (' + trimmed + ')')() as any;
      if (isSocialData(obj)) {
        return { data: obj, wrapper: null };
      }
      // Drill into result/response key which may contain a ```json fence
      const inner = obj?.result ?? obj?.response ?? null;
      if (typeof inner === 'string') {
        const fence = inner.match(/```json\s*([\s\S]*?)```/);
        const jsonStr = fence ? fence[1].trim() : inner.trim();
        try {
          const nested = JSON.parse(jsonStr);
          if (isSocialData(nested)) {
            return { data: nested, wrapper: null };
          }
        } catch (_) { /* fall through */ }
      }
    } catch (_) { /* fall through */ }
  }

  return null;
};

// Serialize the edited SocialMediaData back into the original raw format
const serializeSocialMedia = (data: SocialMediaData, wrapper: any, originalRaw: string): string => {
  const dataJson = JSON.stringify(data, null, 2);

  if (!wrapper) {
    return dataJson;
  }

  // Rebuild with same outer wrapper structure
  const updated = { ...wrapper };
  if (updated.result !== undefined) {
    // Was wrapped in ```json...```
    const fenceMatch = (typeof updated.result === 'string') && updated.result.match(/```json/);
    if (fenceMatch) {
      updated.result = '```json\n' + dataJson + '\n```';
    } else {
      updated.result = dataJson;
    }
  } else if (updated.response !== undefined) {
    updated.response = data;
  }
  return JSON.stringify(updated, null, 2);
};

const PLATFORM_CONFIG: Record<string, { label: string; color: string; accent: string }> = {
  linkedin: { label: 'LinkedIn', color: '#0077b5', accent: '#e8f4fb' },
  twitter: { label: 'Twitter / X', color: '#000000', accent: '#f7f7f7' },
  facebook: { label: 'Facebook', color: '#1877f2', accent: '#e7f0fd' },
  instagram: { label: 'Instagram', color: '#c13584', accent: '#fdf0f7' },
};

const PLATFORMS = ['linkedin', 'twitter', 'facebook', 'instagram'] as const;
const IMAGE_PLATFORMS = ['linkedin', 'facebook', 'instagram'];

// Single platform card editor
const PlatformCard: React.FC<{
  platform: string;
  data: PlatformData;
  onChange: (updated: PlatformData) => void;
  articleImageUrl?: string;
  articleImageAlt?: string;
}> = ({ platform, data, onChange, articleImageUrl, articleImageAlt }) => {
  const cfg = PLATFORM_CONFIG[platform];
  const isTwitter = platform === 'twitter';

  // Main text: post, caption, or tweet
  const mainText = data.post ?? data.caption ?? data.tweet ?? '';
  const setMainText = (val: string) => {
    const key = data.caption !== undefined ? 'caption' : data.tweet !== undefined ? 'tweet' : 'post';
    onChange({ ...data, [key]: val });
  };

  // Twitter thread
  const thread: string[] = data.thread ?? [];
  const setThread = (updated: string[]) => onChange({ ...data, thread: updated });

  // Hashtags
  const hashtags: string[] = data.hashtags ?? [];
  const [tagInput, setTagInput] = useState('');

  const addTag = () => {
    const tag = tagInput.replace(/^#/, '').trim();
    if (tag && !hashtags.includes(tag)) {
      onChange({ ...data, hashtags: [...hashtags, tag] });
    }
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    onChange({ ...data, hashtags: hashtags.filter(t => t !== tag) });
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  return (
    <div className="social-platform-card" style={{ borderTop: `3px solid ${cfg.color}` }}>
      <div className="platform-card-header" style={{ background: cfg.accent }}>
        <span className="platform-card-title" style={{ color: cfg.color }}>{cfg.label}</span>
      </div>

      <div className="platform-card-body">
        {isTwitter && thread.length > 0 ? (
          // Twitter thread mode
          <div className="twitter-thread-editor">
            <label className="field-label">Thread ({thread.length} tweet{thread.length !== 1 ? 's' : ''})</label>
            {thread.map((tweet, idx) => (
              <div key={idx} className="thread-tweet-row">
                <span className="tweet-number">{idx + 1}</span>
                <textarea
                  className="social-textarea tweet-textarea"
                  value={tweet}
                  onChange={e => {
                    const updated = [...thread];
                    updated[idx] = e.target.value;
                    setThread(updated);
                  }}
                  rows={3}
                />
                <button
                  className="remove-tweet-btn"
                  onClick={() => setThread(thread.filter((_, i) => i !== idx))}
                  title="Remove tweet"
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              className="add-tweet-btn"
              onClick={() => setThread([...thread, ''])}
            >
              + Add Tweet
            </button>
          </div>
        ) : (
          // Standard post / caption / tweet
          <div className="field-group">
            <label className="field-label">
              {platform === 'instagram' ? 'Caption' : platform === 'twitter' ? 'Tweet' : 'Post'}
            </label>
            <textarea
              className="social-textarea"
              value={mainText}
              onChange={e => setMainText(e.target.value)}
              rows={6}
              placeholder={`Write your ${platform} post here...`}
            />
            <div className="char-count" style={{ color: platform === 'twitter' && mainText.length > 280 ? '#e53e3e' : '#718096' }}>
              {mainText.length}{platform === 'twitter' ? ' / 280' : ''} characters
            </div>
          </div>
        )}

        {/* CTA */}
        {data.cta !== undefined && (
          <div className="field-group">
            <label className="field-label">Call to Action (CTA)</label>
            <textarea
              className="social-textarea"
              value={data.cta}
              onChange={e => onChange({ ...data, cta: e.target.value })}
              rows={2}
              placeholder="e.g. Read our full article for expert insights! [Link to Article]"
            />
          </div>
        )}

        {/* Hero image indicator for applicable platforms */}
        {IMAGE_PLATFORMS.includes(platform) && articleImageUrl && (
          <div className="field-group platform-image-row">
            <label className="field-label">Hero Image</label>
            <div className="platform-image-preview">
              <img
                src={articleImageUrl}
                alt={articleImageAlt || 'Article hero image'}
                className="platform-image-thumb"
              />
              <span className="platform-image-badge">Will be attached to this post</span>
            </div>
          </div>
        )}

        {/* Hashtags */}
        <div className="field-group">
          <label className="field-label">Hashtags</label>
          <div className="hashtag-editor">
            <div className="hashtag-chips">
              {hashtags.map(tag => (
                <span key={tag} className="hashtag-chip" style={{ borderColor: cfg.color, color: cfg.color }}>
                  #{tag}
                  <button
                    className="remove-tag-btn"
                    onClick={() => removeTag(tag)}
                    title="Remove"
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
            <div className="hashtag-input-row">
              <input
                type="text"
                className="hashtag-input"
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                placeholder="Add tag (Enter or comma)"
              />
              <button className="add-tag-btn" onClick={addTag} style={{ background: cfg.color }}>
                Add
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const EditableSocialMediaViewer: React.FC<EditableSocialMediaViewerProps> = ({
  rawContent,
  onSave,
  onCancel,
  articleImageUrl,
  articleImageAlt,
  taskId,
  onHeroImageChanged,
}) => {
  const [socialData, setSocialData] = useState<SocialMediaData>({});
  const [wrapper, setWrapper] = useState<any>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [parseError, setParseError] = useState(false);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const { canAccessModule } = usePermissions();

  useEffect(() => {
    const result = parseSocialMediaContent(rawContent);
    if (result) {
      setSocialData(result.data);
      setWrapper(result.wrapper);
      setParseError(false);
    } else {
      setParseError(true);
    }
  }, [rawContent]);

  const handlePlatformChange = (platform: string, updated: PlatformData) => {
    setSocialData(prev => ({ ...prev, [platform]: updated }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    const serialized = serializeSocialMedia(socialData, wrapper, rawContent);
    const result = await onSave(serialized);
    setIsSaving(false);
    // A refused save (e.g. ContentPreview's wrapper guard) returns `false` —
    // leave hasChanges set so the editor still shows unsaved changes.
    if (result === false) {
      return;
    }
    setHasChanges(false);
  };

  if (parseError) {
    return (
      <div className="editable-social-media-viewer">
        <div className="parse-error-notice">
          <p>Unable to parse social media content for editing. The raw content is shown below.</p>
          <textarea
            className="social-textarea"
            defaultValue={rawContent}
            rows={20}
            style={{ width: '100%', fontFamily: 'monospace', fontSize: 13 }}
          />
        </div>
        <div className="edit-actions-bar">
          <button className="cancel-btn" onClick={onCancel}>❌ Cancel</button>
        </div>
      </div>
    );
  }

  const presentPlatforms = PLATFORMS.filter(p => socialData[p]);

  return (
    <div className="editable-social-media-viewer">
      {/* Article Details — shared across all platforms */}
      <div className="article-details-panel">
        <h3 className="article-details-title">Article Details</h3>

        {/* Hero Image field — only shown when taskId is provided */}
        <div className="field-group">
          <label className="field-label">Hero Image</label>
          <div className="hero-image-preview-row">
            {articleImageUrl ? (
              <img src={articleImageUrl} alt={articleImageAlt || ''} className="hero-image-preview-thumb" />
            ) : (
              <div className="hero-image-preview-empty">No hero image set</div>
            )}
            {taskId && (
              <button
                className="hero-image-edit-btn"
                onClick={() => setImagePickerOpen(true)}
                title="Saves immediately — independent of the post editor's Save/Cancel"
              >
                {articleImageUrl ? 'Change Hero Image' : 'Add Hero Image'}
              </button>
            )}
          </div>
        </div>

        <div className="field-group">
          <label className="field-label">Article URL</label>
          <input
            type="url"
            className="article-url-input"
            value={socialData.article_url || ''}
            onChange={e => {
              setSocialData(prev => ({ ...prev, article_url: e.target.value }));
              setHasChanges(true);
            }}
            placeholder="https://yoursite.com/article-slug"
          />
          <div className="field-hint">Used to replace [Link to Article] placeholders when publishing to all platforms.</div>
        </div>
      </div>

      {/* Image picker modal — only mounted when taskId is available */}
      {imagePickerOpen && taskId && (
        <ImagePickerModal
          open={imagePickerOpen}
          onClose={() => setImagePickerOpen(false)}
          title="Hero Image"
          aiPlatform="instagram"
          showAiTab={canAccessModule('multichannel')}
          pexelsFetcher={async (_query, excludedIds) => {
            const result = await selectImagesForTask(taskId, true, excludedIds);
            const hero = result.images?.hero;
            if (!hero) throw new Error('No image returned from Pexels.');
            const url = hero.urls?.large2x || hero.urls?.large || hero.url || hero.src || '';
            return { image_url: url, pexels_id: hero.pexels_id, photographer: hero.photographer };
          }}
          onImageSelected={async (url, meta) => {
            await updateHeroImage(taskId, url, meta.photographer || '', meta.source);
            onHeroImageChanged?.(url);
          }}
        />
      )}

      <div className="social-editor-platforms">
        {presentPlatforms.map(platform => (
          <PlatformCard
            key={platform}
            platform={platform}
            data={socialData[platform]!}
            onChange={updated => handlePlatformChange(platform, updated)}
            articleImageUrl={articleImageUrl}
            articleImageAlt={articleImageAlt}
          />
        ))}
      </div>

      <div className="edit-actions-bar">
        <button className="cancel-btn" onClick={onCancel} disabled={isSaving}>
          ❌ Cancel
        </button>
        {hasChanges && (
          <span style={{ color: '#e53e3e', fontSize: '14px' }}>Unsaved changes</span>
        )}
        <button
          className="save-btn"
          onClick={handleSave}
          disabled={isSaving || !hasChanges}
        >
          {isSaving ? 'Saving...' : '💾 Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default EditableSocialMediaViewer;
